// Reactions + comments for radio items (episodes and tracks/videos).
// Reads are public; mutations require Supabase auth.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const REACTIONS = ["like", "love", "insightful", "thinking", "fire"] as const;
export type RadioReaction = (typeof REACTIONS)[number];
const KIND = z.enum(["episode", "track"]);

const ItemRef = z.object({
  itemId: z.string().uuid(),
  itemKind: KIND,
});

export const getRadioSocial = createServerFn({ method: "GET" })
  .inputValidator((input: { itemId: string; itemKind: "episode" | "track" }) =>
    ItemRef.parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const [reactRes, commentRes] = await Promise.all([
      (supabaseAdmin as any)
        .from("radio_reactions")
        .select("reaction,user_id")
        .eq("item_id", data.itemId),
      (supabaseAdmin as any)
        .from("radio_comments")
        .select("id,author_name,body,created_at")
        .eq("item_id", data.itemId)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    const counts: Record<RadioReaction, number> = {
      like: 0,
      love: 0,
      insightful: 0,
      thinking: 0,
      fire: 0,
    };
    for (const r of (reactRes.data ?? []) as { reaction: RadioReaction }[]) {
      if (r.reaction in counts) counts[r.reaction]++;
    }
    return {
      counts,
      total: (reactRes.data?.length ?? 0) as number,
      comments: (commentRes.data ?? []) as Array<{
        id: string;
        author_name: string;
        body: string;
        created_at: string;
      }>,
    };
  });

export const getMyRadioReaction = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { itemId: string; itemKind: "episode" | "track" }) =>
    ItemRef.parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: row } = await (context.supabase as any)
      .from("radio_reactions")
      .select("reaction")
      .eq("item_id", data.itemId)
      .eq("user_id", context.userId)
      .maybeSingle();
    return { reaction: (row?.reaction as RadioReaction | null) ?? null };
  });

export const getMyRadioCommentIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { itemId: string; itemKind: "episode" | "track" }) =>
    ItemRef.parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: rows } = await (context.supabase as any)
      .from("radio_comments")
      .select("id")
      .eq("item_id", data.itemId)
      .eq("user_id", context.userId);
    return { ids: ((rows ?? []) as { id: string }[]).map((r) => r.id) };
  });

export const setMyRadioReaction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      itemId: string;
      itemKind: "episode" | "track";
      reaction: RadioReaction | null;
    }) =>
      ItemRef.extend({ reaction: z.enum(REACTIONS).nullable() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.reaction === null) {
      const { error } = await (supabase as any)
        .from("radio_reactions")
        .delete()
        .eq("item_id", data.itemId)
        .eq("user_id", userId);
      if (error) throw new Error(error.message);
      return { ok: true, reaction: null };
    }
    const { error } = await (supabase as any).from("radio_reactions").upsert(
      {
        item_id: data.itemId,
        item_kind: data.itemKind,
        user_id: userId,
        reaction: data.reaction,
      },
      { onConflict: "item_id,user_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, reaction: data.reaction };
  });

export const addRadioComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { itemId: string; itemKind: "episode" | "track"; body: string }) =>
      ItemRef.extend({ body: z.string().trim().min(2).max(2000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: profile } = await (supabase as any)
      .from("profiles")
      .select("full_name")
      .eq("id", userId)
      .maybeSingle();
    const authorName =
      (profile?.full_name as string | undefined)?.trim() || "Listener";
    const { data: row, error } = await (supabase as any)
      .from("radio_comments")
      .insert({
        item_id: data.itemId,
        item_kind: data.itemKind,
        user_id: userId,
        author_name: authorName,
        body: data.body,
      })
      .select("id,user_id,author_name,body,created_at")
      .single();
    if (error) throw new Error(error.message);
    return { comment: row };
  });

export const deleteMyRadioComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { commentId: string }) =>
    z.object({ commentId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any)
      .from("radio_comments")
      .delete()
      .eq("id", data.commentId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
